import React, { useState } from "react";
import { api, type Me } from "./api";
import { formatPhone } from "../server/phone";

/**
 * My account (every signed-in user): the user's own details, read-only, and
 * the one thing they may change themselves, their cell phone. The server only
 * ever changes the session's own row (PUT /api/account/phone).
 */
export function Account({ me, onSaved }: { me: Me; onSaved: (user: Me) => void }): React.ReactElement {
  const [phone, setPhone] = useState(formatPhone(me.phone));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    const r = await api<{ user: Me }>("PUT", "/api/account/phone", { phone });
    setBusy(false);
    if (!r.ok) {
      setError(r.data.error ?? "Save failed.");
      return;
    }
    setPhone(formatPhone(r.data.user.phone));
    setSaved(true);
    onSaved(r.data.user);
  }

  return (
    <section>
      <h2 className="mb-4 text-xl font-semibold">My account</h2>
      <div className="section-card">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
          <dt className="text-slate-500">Name</dt>
          <dd>{me.name}</dd>
          <dt className="text-slate-500">Email</dt>
          <dd className="break-all">{me.email}</dd>
          <dt className="text-slate-500">Role</dt>
          <dd>{me.roleLabel}</dd>
          <dt className="text-slate-500">Credential</dt>
          <dd>{me.credential || "—"}</dd>
          <dt className="text-slate-500">Licensed</dt>
          <dd>{me.licensed ? "Yes" : "No"}</dd>
          <dt className="text-slate-500">May give OTC</dt>
          <dd>{me.mayAdministerOtc ? "Yes" : "No"}</dd>
        </dl>
        <p className="field-hint mt-3">A System Admin changes these. Ask one if something is wrong.</p>
      </div>
      <form className="section-card" onSubmit={submit} noValidate>
        <label htmlFor="my-phone" className="field-label">
          Cell phone
        </label>
        <input
          id="my-phone"
          className="field-input max-w-xs"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="(410) 555-0123"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            setSaved(false);
          }}
          aria-describedby={error ? "my-phone-error" : "my-phone-hint"}
        />
        <p id="my-phone-hint" className="field-hint">US number. Leave it blank to remove it.</p>
        {error && (
          <p id="my-phone-error" className="field-error" role="alert">
            {error}
          </p>
        )}
        {saved && (
          <p className="mt-2 text-sm text-green-700" role="status">
            Saved.
          </p>
        )}
        <button type="submit" className="btn-primary mt-4 w-full sm:w-auto" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </section>
  );
}
